import MainTabButton from './MainTabButton';
import Button from '@/components/Button';
import SwiperBasic from '@/components/Swiper/SwiperBasic';
import { breakpoint } from '@/helpers/BreakpointHelper';
import React, {
  FC,
  useCallback,
  useState,
  useEffect,
  useRef,
  useMemo,
} from 'react';
import styled from 'styled-components';
import SwiperCore, {
  A11y,
  Navigation,
  Scrollbar,
  Swiper as SwiperClass,
} from 'swiper';
import 'swiper/components/navigation/navigation.min.css';
import 'swiper/components/pagination/pagination.min.css';
import 'swiper/components/scrollbar/scrollbar.min.css';
import 'swiper/swiper.min.css';
import { Swiper, SwiperSlide } from 'swiper/react';

SwiperCore.use([Navigation, Scrollbar, A11y]);

const PannelContainer = styled.div`
  overflow-x: auto;
`;

const UiTabPanel = styled.ul`
  background: #fff;
  display: flex;
  gap: 15px;

  ${MainTabButton} {
    &.active {
      background: #1cabe2;
      color: #fff;
    }
  }

  ${breakpoint(640)} {
    flex-wrap: wrap;
  }

  & > li {
    white-space: nowrap;
  }
  &.ui-tabpanel-list2 {
    margin-top: 16px;
    gap: 12px;
    ${breakpoint(640)} {
      gap: 8px;
    }
  }
`;
const CategoryList = styled.li``;
const SubTabButton = styled(Button)`
  font-size: 16px;
  font-weight: 400;
  color: #828385;
  height: auto;
  padding: 9px 20px;
  background: #fff;
  border: 1px solid #e5e6e8;
  &.active {
    color: #1cabe2;
    border-color: #1cabe2;
  }

  ${breakpoint(640)} {
    font-size: 14px;
    padding: 5px 20px;
  }
`;
const PartnerListWrap = styled.div`
  margin-top: 64px;
`;
const PartnerList = styled.div`
  font-size: 0;
  overflow: hidden;

  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 32px;

  ${breakpoint(1280)} {
    gap: 0px;
  }
`;

const SwiperPartner = styled(SwiperBasic)`
  padding: 0;

  .scroll-only .swiper-scrollbar {
    width: auto;
    left: 16px;
    margin-left: 0;
    transform: translate(0, 0);
    right: 218px;
  }

  ${breakpoint(`tablet`)} {
    .scroll-only .swiper-scrollbar {
      width: calc(100% - 40px);
      left: 20px;
    }
    .header-dec {
      margin-top: 32px;
    }
  }
`;
const PartnerItem = styled.div`
  display: inline-block;
  position: relative;
  width: 198px;
  height: 198px;

  .box {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    z-index: 1;
    position: relative;
    &::before {
      content: '';
      display: block;
      border: 0 solid transparent;
      position: absolute;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      transition: border 0.3s;
    }
  }
  &:hover {
    .box {
      &::before {
        border: 12px solid #1cabe2;
      }
    }
  }
  ${breakpoint(1280)} {
    width: 24%;
    height: 23.83vw;
  }
  ${breakpoint(800)} {
    width: 32.33%;
    height: 30.756vw;
  }
  ${breakpoint(640)} {
    width: 49%;
    height: 46.012vw;
  }
`;

interface PartnerListProps {
  categories: CategoryData[];
  partners: PartnerData[];
}
interface StateData {
  contentKey?: null | string;
}
export interface CategoryData {
  id: number | string;
  categoryCode?: number | string;
  categoryCodeName?: string;
  contentKey?: any;
}
export interface PartnerData {
  id: string | number;
  indexNumber?: string | number;
  title?: string;
  category1?: string;
  category2?: string;
  url?: string;
  year?: string | number;
  desc?: string;
  imgPath?: string;
  csrDetailUrl?: string;
}

export const ContributionLogo: FC<PartnerListProps> = ({
  partners,
  categories,
}) => {
  const [contentIndex, setContentIndex] = useState(0);
  const swiperRef = useRef<SwiperClass | null>(null);

  const [itemsPerSlide, setItemsPerSlide] = useState(10);
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;

      if (width <= 640) {
        setItemsPerSlide(8);
      } else if (width <= 800) {
        setItemsPerSlide(9);
      } else if (width <= 1280) {
        setItemsPerSlide(8);
      } else {
        setItemsPerSlide(10);
      }
    };

    handleResize(); // 초기 실행
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [selectedCategory1, setSelectedCategory1] = useState<string>('기업'); // 선택된 category1
  const [selectedCategory2, setSelectedCategory2] = useState<string>('전체'); // 선택된 category2

  // category1 중복 제거 리스트
  const uniqueCategory1List = Array.from(
    new Set(partners.map((p) => p.category1).filter(Boolean)),
  );

  // 선택된 category1에 해당하는 category2 리스트
  const category2List = partners
    .filter((p) => p.category1 === selectedCategory1)
    .map((p) => p.category2)
    .filter(Boolean);
  const uniqueCategory2List = Array.from(new Set(category2List));

  const filteredPartners = useMemo(() => {
    // category2가 "전체"가 아닌 경우만 필터링
    if (
      selectedCategory1 &&
      selectedCategory2 &&
      selectedCategory2 !== '전체'
    ) {
      return partners.filter(
        (p) =>
          p.category1 === selectedCategory1 &&
          p.category2 === selectedCategory2,
      );
    }

    // category1만 선택된 경우
    if (selectedCategory1) {
      return partners.filter((p) => p.category1 === selectedCategory1);
    }

    // 아무것도 선택 안 된 경우 전체 출력
    return partners;
  }, [partners, selectedCategory1, selectedCategory2]);

  useEffect(() => {
    swiperRef.current?.slideTo(0);
  }, [uniqueCategory1List]);

  return (
    <>
      <PannelContainer>
        <UiTabPanel className="ui-tabpanel-list">
          {/* category1 버튼 */}
          {uniqueCategory1List.map((category1) => (
            <CategoryList key={category1}>
              <MainTabButton
                className={`${selectedCategory1 === category1 ? 'active' : ''}`}
                active={selectedCategory1 === category1}
                onClick={() => {
                  setSelectedCategory1(category1 as string);
                  setSelectedCategory2('전체');
                }}
              >
                {category1}
              </MainTabButton>
            </CategoryList>
          ))}
        </UiTabPanel>
        {selectedCategory1 === '기업' && (
          <UiTabPanel className="ui-tabpanel-list2">
            {/* category2 버튼 */}
            <CategoryList>
              <SubTabButton
                outline
                shape="round"
                className={`${selectedCategory2 === '전체' ? 'active' : ''}`}
                active={selectedCategory2 === '전체'}
                color={selectedCategory2 === '전체' ? 'blue' : 'gray'}
                onClick={() => setSelectedCategory2('전체')}
              >
                전체
              </SubTabButton>
            </CategoryList>
            {uniqueCategory2List.length > 0 &&
              uniqueCategory2List.map((category2) => (
                <CategoryList key={category2}>
                  <SubTabButton
                    outline
                    shape="round"
                    className={`${
                      selectedCategory2 === category2 ? 'active' : ''
                    }`}
                    active={selectedCategory2 === category2}
                    color={selectedCategory2 === category2 ? 'blue' : 'gray'}
                    onClick={() => {
                      setSelectedCategory2(category2 as string);
                    }}
                  >
                    {category2}
                  </SubTabButton>
                </CategoryList>
              ))}
          </UiTabPanel>
        )}
      </PannelContainer>

      <PartnerListWrap
        className={`ui-tabpanel-contents ui-tabpanel-contents-${contentIndex}`}
      >
        <SwiperPartner overflowHidden>
          <Swiper
            onSwiper={(swiper) => {
              swiperRef.current = swiper;
            }}
            slidesPerView="auto"
            navigation
            scrollbar={{ draggable: true }}
            observer
            observeParents
            className="scroll-only"
          >
            {Array.from({
              length: Math.ceil(filteredPartners.length / itemsPerSlide),
            }).map((_, slideIdx) => (
              <SwiperSlide key={`slide-${slideIdx}`}>
                <PartnerList className="partner-grid">
                  {filteredPartners
                    .slice(
                      slideIdx * itemsPerSlide,
                      slideIdx * itemsPerSlide + itemsPerSlide,
                    )
                    .map((partner, idx) => {
                      console.log(partner);
                      return (
                        <PartnerItem key={`partner-${slideIdx}-${idx}`}>
                          111 {partner.csrDetailUrl}
                          {partner.csrDetailUrl ? (
                            <a
                              className="box"
                              href={partner.csrDetailUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                            >
                              <img src={partner.imgPath} alt={partner.title} />
                            </a>
                          ) : (
                            <div className="box">
                              <img src={partner.imgPath} alt={partner.title} />
                            </div>
                          )}
                        </PartnerItem>
                      );
                    })}
                </PartnerList>
              </SwiperSlide>
            ))}
          </Swiper>
        </SwiperPartner>
      </PartnerListWrap>
    </>
  );
};

export default ContributionLogo;
